import { Reducer } from 'redux';

import {
  HubOwnerStore,
  HubOwnerStoreAction,
  HubOwnerStoreActionTypes,
} from './types';

const initialState: HubOwnerStore = {
  importMedicationRollSuccess: false,
  hubOwners: [],
  isLoading: false,
  addHubOwnerSuccess: false,
  isAddingHubOwner: false,
  actionStatus: {
    success: false,
    error: null,
    data: null,
    apiAction: null,
  },
  ownerContacts: [],
  medicationStats: null,
  medicationStatsOwnerId: null,
  ownerOverview: null,
  ownerOverviewLoading: false,
  ownerSurveyInsights: null,
  ownerSurveyInsightsLoading: false,
  ownerSurveyInsightsError: null,
  ownerSurveyInsightsRequestId: 0,
  careteamRoleLoading: false,
  deleteContactLoading: false,
  ownerContactsLoading: false,
};

const hubOwnerReducer: Reducer<HubOwnerStore, HubOwnerStoreAction> = (
  state = initialState,
  action,
): HubOwnerStore => {
  switch (action.type) {
    case HubOwnerStoreActionTypes.CREATE_MEDICATION_ROLL_SUCCESS: {
      let newState = {
        ...state,
        importMedicationRollSuccess: true,
      };
      return newState;
    }
    case HubOwnerStoreActionTypes.GET_MEDICATION_SCHEDULE_TEMPLATE: {
      const { medScheduleTemplate } = action.payload;

      let newState = {
        ...state,
        medScheduleTemplate: medScheduleTemplate,
      };
      return newState;
    }
    case HubOwnerStoreActionTypes.RESET_IMPORT_MED_ROLL_FORM: {
      return {
        ...state,
        importMedicationRollSuccess: false,
      };
    }
    case HubOwnerStoreActionTypes.GET_MEDICATION_SCHEDULE: {
      const { medSchedule, schedule_local_timezone, med_window_time_sec } =
        action.payload;
      return {
        ...state,
        medSchedule: medSchedule,
        schedule_local_timezone: schedule_local_timezone,
        med_window_time_sec: med_window_time_sec,
      };
    }
    case HubOwnerStoreActionTypes.GET_MEDICATION_EVENT_HISTORY: {
      const { medEventHistory, ownerId, medEventId } = action.payload;

      return {
        ...state,
        medEventHistory: {
          ownerId,
          medEventId,
          history: medEventHistory,
        },
      };
    }
    case HubOwnerStoreActionTypes.REQUEST_MEDICATION_EVENT_IMAGES: {
      return {
        ...state,
        medEventImagesRequestId: action.payload.requestId,
      };
    }
    case HubOwnerStoreActionTypes.GET_MEDICATION_EVENT_IMAGES: {
      const { medEventImages, ownerId, medEventId, requestId } = action.payload;

      // A newer request has been issued since this one started (e.g. the
      // popup was re-anchored to another event). This response is stale;
      // drop it so it can't overwrite the newer event's images.
      if (requestId !== state.medEventImagesRequestId) return state;

      return {
        ...state,
        medEventImages: {
          ownerId,
          medEventId,
          images: medEventImages,
        },
      };
    }
    case HubOwnerStoreActionTypes.SET_LOADING: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }
    case HubOwnerStoreActionTypes.UNSET_LOADING: {
      return {
        ...state,
        isLoading: false,
      };
    }
    case HubOwnerStoreActionTypes.FETCH_HUB_OWNERS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        hubOwners: action.payload,
      };
    }
    case HubOwnerStoreActionTypes.ADD_HUB_OWNER_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        addHubOwnerSuccess: true,
      };
    }
    case HubOwnerStoreActionTypes.RESET_ADD_HUB_OWNER_SUCCESS: {
      return {
        ...state,
        addHubOwnerSuccess: false,
      };
    }
    case HubOwnerStoreActionTypes.EDIT_OWNER_SUCCESS: {
      const { ownerId, requestBody } = action.payload;
      // requestBody holds only the changed fields; gender_id/date_of_birth/
      // med_window_time_sec don't map onto the HubOwner list shape and are
      // reconciled by the post-save refetch.
      const { gender_id, date_of_birth, med_window_time_sec, ...ownerPatch } =
        requestBody;
      const updatedOwners = state?.hubOwners?.map((owner) =>
        owner.customer_id === ownerId ? { ...owner, ...ownerPatch } : owner,
      );
      return {
        ...state,
        hubOwners: updatedOwners as typeof state.hubOwners,
      };
    }

    case HubOwnerStoreActionTypes.SET_ACTION_STATUS:
      return {
        ...state,
        actionStatus: {
          ...state.actionStatus,
          // Duplicate-owner follow-up is only meaningful for the status
          // that produced it — never let it merge into a later status
          errorAction: null,
          ...action.payload,
        },
      };
    case HubOwnerStoreActionTypes.CLEAR_ACTION_STATUS:
      return {
        ...state,
        actionStatus: initialState.actionStatus,
      };
    case HubOwnerStoreActionTypes.SET_ADDING_HUB_OWNER:
      return {
        ...state,
        isAddingHubOwner: action.payload,
      };

    case HubOwnerStoreActionTypes.FETCH_OWNER_CONTACTS_SUCCESS: {
      return {
        ...state,
        ownerContacts: action.payload,
      };
    }

    case HubOwnerStoreActionTypes.FETCH_MEDICATION_LIST_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        medicationList: action.payload.medications,
        medicationStats: action.payload.stats,
        medicationStatsOwnerId: action.payload.ownerId,
      };
    }
    case HubOwnerStoreActionTypes.FETCH_OWNER_OVERVIEW_SUCCESS: {
      return {
        ...state,
        ownerOverview: action.payload,
        ownerOverviewLoading: false,
      };
    }
    case HubOwnerStoreActionTypes.SET_OWNER_OVERVIEW_LOADING: {
      return {
        ...state,
        ownerOverviewLoading: action.payload,
      };
    }
    case HubOwnerStoreActionTypes.REQUEST_OWNER_SURVEY_INSIGHTS: {
      return {
        ...state,
        ownerSurveyInsightsLoading: true,
        ownerSurveyInsightsError: null,
        ownerSurveyInsightsRequestId: action.payload.requestId,
      };
    }
    case HubOwnerStoreActionTypes.FETCH_OWNER_SURVEY_INSIGHTS_SUCCESS: {
      // A newer request (other patient or window) superseded this one.
      if (action.payload.requestId !== state.ownerSurveyInsightsRequestId) {
        return state;
      }
      const { requestId: _requestId, ...insights } = action.payload;
      return {
        ...state,
        ownerSurveyInsights: insights,
        ownerSurveyInsightsLoading: false,
        ownerSurveyInsightsError: null,
      };
    }
    case HubOwnerStoreActionTypes.FETCH_OWNER_SURVEY_INSIGHTS_FAILURE: {
      if (action.payload.requestId !== state.ownerSurveyInsightsRequestId) {
        return state;
      }
      return {
        ...state,
        ownerSurveyInsightsLoading: false,
        ownerSurveyInsightsError: action.payload.error,
      };
    }

    case HubOwnerStoreActionTypes.SET_CARETEAM_ROLE_LOADING: {
      return {
        ...state,
        careteamRoleLoading: action.payload,
      };
    }

    case HubOwnerStoreActionTypes.SET_DELETE_CONTACT_LOADING:
      return {
        ...state,
        deleteContactLoading: action.payload,
      };

    case HubOwnerStoreActionTypes.SET_OWNER_CONTACTS_LOADING:
      return {
        ...state,
        ownerContactsLoading: action.payload,
      };

    default:
      return state;
  }
};

export default hubOwnerReducer;
